// @ts-nocheck
/*
 * Port of disposition-forms/list.js (1374 lines). BYTE-IDENTICAL.
 * Disposition forms list with per-row Set Default / Set System Default,
 * View form (fields definition) modal, Submissions modal (a second table
 * of `disposition_form_data` rows with an inline Edit Submission modal
 * that is agent-only, gated by role AND a 24-hour edit window since
 * `created_at`), and CSV export. Super Admin sees "Default disposition
 * wrap-up time" bulk-config panel above the list.
 * Roles:
 *   - CALL_CENTRE_AGENT (non-team_lead) â†’ self-scoped: only sees own
 *     `disposition_form_data` submissions, sees `Action` col to edit.
 *   - CALL_CENTRE_ADMIN / TEAM_LEAD / SUPERADMIN â†’ sees edit-audit cols
 *     (Auto Submit / Edited / Edited On / Edited By).
 * PrimeReact DataTable â†’ obd3-gui DataTable + tanstack ColumnDef.
 * EditIcon/DeleteIcon inlined (same shape as remarks/RemarkStatusListPage).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card as ShadCard } from '@/components/ui/card';
import { Button as _ShadButton } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Row: any = ({ className, children, ...props }: any) => (
  <div className={_cx('row', className)} {...props}>
    {children}
  </div>
);
const _colCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts = [];
  if (md) parts.push(`col-md-${md}`);
  if (lg) parts.push(`col-lg-${lg}`);
  if (xl) parts.push(`col-xl-${xl}`);
  if (sm) parts.push(`col-sm-${sm}`);
  if (xs) parts.push(`col-${xs}`);
  if (!parts.length) parts.push('col');
  return parts.join(' ');
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={_cx(_colCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const _btnVariantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  light: 'outline',
  link: 'link',
};
const Button: any = ({ variant, size, className, children, ...props }: any) => {
  let mapped = variant;
  if (typeof variant === 'string') {
    if (variant.startsWith('outline-')) mapped = 'outline';
    else if (_btnVariantMap[variant]) mapped = _btnVariantMap[variant];
    else if (variant.startsWith('btn ')) mapped = 'default';
  }
  return (
    <_ShadButton variant={mapped} size={size} className={className} {...props}>
      {children}
    </_ShadButton>
  );
};
const Card: any = ({ className, children, ...props }: any) => (
  <ShadCard className={className} {...props}>
    {children}
  </ShadCard>
);
Card.Header = ({ className, children, ...props }: any) => (
  <div className={_cx('card-header', className)} {...props}>
    {children}
  </div>
);
Card.Body = ({ className, children, ...props }: any) => (
  <div className={_cx('card-body', className)} {...props}>
    {children}
  </div>
);
Card.Title = ({ as: As = 'h5', className, children, ...props }: any) => (
  <As className={_cx('card-title', className)} {...props}>
    {children}
  </As>
);
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={_cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={_cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, isInvalid, ...props }: any) => (
  <select
    className={_cx('form-control form-select', isInvalid && 'is-invalid', className)}
    {...props}
  >
    {children}
  </select>
);
Form.Check = ({
  type = 'checkbox',
  label,
  id,
  className,
  inline,
  children,
  ...props
}: any) => (
  <div className={_cx('form-check', inline && 'form-check-inline', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && (
      <label htmlFor={id} className="form-check-label">
        {label}
      </label>
    )}
    {children}
  </div>
);
const Modal: any = ({ show, onHide, size, centered: _c, children, className, ...props }: any) => {
  const sizeCls =
    size === 'sm'
      ? 'sm:max-w-md'
      : size === 'lg'
      ? 'sm:max-w-2xl'
      : size === 'xl'
      ? 'sm:max-w-6xl'
      : '';
  return (
    <Dialog open={!!show} onOpenChange={(o: boolean) => !o && onHide?.()}>
      <DialogContent
        // This page also renders inside cti2's Agent Portal (My Dashboard →
        // Disposition Forms), which portals its whole shell straight onto
        // <body> at z-[1000] (AgentPortalPage.tsx) — above this Dialog's
        // default z-50, so without an explicit override every modal here
        // (View / Submissions / Edit) opens correctly in the DOM but renders
        // invisibly behind that shell. The !important beats it regardless
        // of which utility Tailwind happens to emit later in the stylesheet.
        className={`${sizeCls} max-h-[90vh] overflow-y-auto !z-[2000] ${className || ''}`.trim()}
        overlayClassName="!z-[2000]"
        {...props}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
};
Modal.Header = ({ children, closeButton: _cb, className, ...props }: any) => (
  <DialogHeader className={className} {...props}>
    {children}
  </DialogHeader>
);
Modal.Title = ({ children, className, ...props }: any) => (
  <DialogTitle className={className} {...props}>
    {children}
  </DialogTitle>
);
Modal.Body = ({ children, className, ...props }: any) => (
  <div className={`overflow-x-auto ${className || ''}`.trim()} {...props}>
    {children}
  </div>
);
Modal.Footer = ({ children, className, ...props }: any) => (
  <DialogFooter className={className} {...props}>
    {children}
  </DialogFooter>
);
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/data-table/DataTable';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import Loader from '@/components/partials/components/Loader';
import RenderDeleteModal from '@/components/partials/components/delete-modal';
import { getApiCall, postApiCall, patchApiCall } from '@/service/httpCall';
import {
  API_DISPOSITION_DELETE,
  API_DISPOSITION_LIST,
  API_DISPOSITION_SET_DEFAULT,
  API_DISPOSITION_SET_SYSTEM_DEFAULT,
  API_DISPOSITION_TIMING_DEFAULTS,
  API_DISPOSITION_FORM_DATA_UPDATE,
  API_REMARK_STATUS_LIST,
} from '@/service/url';
import { getUser } from '@/utilities/setting';
import { MODULE_ACL, ROUTES, USER_ROLES } from '@/utilities/constant';

const DISPOSITION_WRITE_ROLES = [
  USER_ROLES.SUPERADMIN,
  USER_ROLES.CALL_CENTRE_ADMIN,
];
import { exportToCSV, hasPermission } from '@/utilities/helpers';
import {
  formatDispositionCallDirectionLabel,
  wrapUpTimingDirection,
} from '@/utilities/dispositionCallDirection';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Download, Plus, RefreshCw, Pencil, Trash2, Star, Globe, Eye, Table2, ClipboardList, User, Users } from 'lucide-react';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';
import { CalendarDateInput } from '@/components/ui/calendar-inputs';

const SUBMISSIONS_MAX_MONTHS_BACK = 3;
const formatDateOnlyLocal = (date: Date) => {
  const off = date.getTimezoneOffset();
  return new Date(date.getTime() - off * 60000).toISOString().slice(0, 10);
};
const getSubmissionsBounds = () => {
  const now = new Date();
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - ((now.getDay() + 6) % 7));
  const min = new Date(now);
  min.setMonth(min.getMonth() - SUBMISSIONS_MAX_MONTHS_BACK);
  return {
    weekStart: formatDateOnlyLocal(weekStart),
    today: formatDateOnlyLocal(now),
    min: formatDateOnlyLocal(min),
  };
};

const EditIcon = ({ linkTo }: any) => (
  <Link
    to={linkTo}
    title="Edit"
    className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition hover:bg-primary/15"
  >
    <Pencil className="h-3.5 w-3.5" />
  </Link>
);

const DeleteIcon = ({ handleCallback }: any) => (
  <button
    type="button"
    title="Delete"
    onClick={handleCallback}
    className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200 transition hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:ring-rose-900/60"
  >
    <Trash2 className="h-3.5 w-3.5" />
  </button>
);

const EXPORT_MODEL = 'DispositionForm';

const FOLLOW_UP_DATA_KEYS = new Set([
  'follow_up_enabled',
  'follow_up_date',
  'follow_up_time',
]);

const TYPE_LABELS: any = {
  String: 'Text',
  Textarea: 'Text Area',
  Integer: 'Numbers',
  Float: 'Decimal Values',
  Boolean: 'Checkbox',
};

const isSelfScopedAgent = (user: any) =>
  user?.role === USER_ROLES.CALL_CENTRE_AGENT && user?.agentData?.access !== 'team_lead';

// Roles that see the edit-audit columns (edited / edited on / edited by).
const canSeeEditMeta = (user: any) =>
  [
    USER_ROLES.CALL_CENTRE_ADMIN,
    USER_ROLES.CALL_CENTRE_TEAM_LEAD,
    USER_ROLES.SUPERADMIN,
  ].includes(user?.role);

// Agents may edit their own submissions only within 24 hours of submitting.
const EDIT_WINDOW_MS = 24 * 60 * 60 * 1000;
const isWithinEditWindow = (row: any) => {
  if (!row?.created_at) return false;
  const created = new Date(row.created_at).getTime();
  if (Number.isNaN(created)) return false;
  return Date.now() - created <= EDIT_WINDOW_MS;
};

const getCreatedByLabel = (rowData: any) => {
  const username = rowData?.created_by_user?.username;
  if (username) return username;
  const id = rowData?.created_by_user?.id;
  return id ? String(id) : '—';
};

const DispositionFormsList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [dataList, setDataList] = useState<any[]>([]);
  const [originalData, setOriginalData] = useState<any[]>([]);
  const [search, setSearch] = useState('');

  const [showViewModal, setShowViewModal] = useState(false);
  const [viewRow, setViewRow] = useState<any>(null);

  const [showSubmissionsModal, setShowSubmissionsModal] = useState(false);
  const [submissionsRow, setSubmissionsRow] = useState<any>(null);
  const [submissionsSearch, setSubmissionsSearch] = useState('');
  const [submissionsSourceFilter, setSubmissionsSourceFilter] = useState('');
  const [submissionsExportDisabled, setSubmissionsExportDisabled] = useState(false);
  const [submissionsFrom, setSubmissionsFrom] = useState('');
  const [submissionsTo, setSubmissionsTo] = useState('');
  const [submissionsLoading, setSubmissionsLoading] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editRow, setEditRow] = useState<any>(null);
  const [editValues, setEditValues] = useState<any>({});
  const [editMaskedFields, setEditMaskedFields] = useState<Set<string>>(new Set());
  // Remarks Status here mirrors the live-call disposition card
  // (DispositionCard.tsx): tappable chips from the same admin/SA-managed
  // remarkStatusList, PLUS a free-typed "Others" field for anything not in
  // that list — instead of one merged, fully-editable comma-separated
  // string that showed none of the actual admin/SA-defined options.
  const [editSelectedOutcomes, setEditSelectedOutcomes] = useState<string[]>([]);
  const [editOthersText, setEditOthersText] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [remarkStatusList, setRemarkStatusList] = useState<any[]>([]);

  useEffect(() => {
    getApiCall(API_REMARK_STATUS_LIST, {})
      .then((res: any) => {
        const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        setRemarkStatusList(list.filter((item: any) => item.status === 'Active' && !item.is_deleted));
      })
      .catch(() => setRemarkStatusList([]));
  }, []);

  const remarkOutcomeOptions = useMemo(
    () => remarkStatusList.map((r: any) => r?.remark || r?.name || String(r)).filter(Boolean),
    [remarkStatusList],
  );

  const [enableDeleteModal, setEnableDeleteModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteId, setDeleteId] = useState<any>(null);
  const [defaultLoadingId, setDefaultLoadingId] = useState<any>(null);
  const [systemDefaultLoadingId, setSystemDefaultLoadingId] = useState<any>(null);
  const [timingDefaults, setTimingDefaults] = useState<any>({
    inbound: '60',
    outbound: '60',
  });
  const [timingDefaultsSaving, setTimingDefaultsSaving] = useState(false);

  const [buttonDisable, setButtonDisable] = useState(false);
  const [isSuperAdmin, setIsSuperadmin] = useState(false);
  const [isAgent, setIsAgent] = useState(false);
  const [loggedInUserACL, setLoggedInUserACL] = useState<any>({});
  const [ownerTab, setOwnerTab] = useState<'mine' | 'child'>('mine');
  const [tabLoading, setTabLoading] = useState(false);
  const ownerTabRef = useRef<'mine' | 'child'>('mine');
  const listReqRef = useRef(0);

  const fetchData = async (userArg?: any, scopeArg?: 'mine' | 'child') => {
    const user = userArg || getUser();
    const reqId = ++listReqRef.current;
    const { weekStart, today } = getSubmissionsBounds();
    const params: any = { from_date: weekStart, to_date: today };
    if (user?.role === USER_ROLES.SUPERADMIN) {
      params.scope = scopeArg || ownerTabRef.current || 'mine';
    }
    return getApiCall(API_DISPOSITION_LIST, params)
      .then((resp: any) => {
        if (reqId !== listReqRef.current) return [];
        const updated = Array.isArray(resp)
          ? resp.map((val: any, idx: number) => ({ ...val, key: idx + 1 }))
          : [];
        setDataList(updated);
        setOriginalData(updated);
        return updated;
      })
      .catch((err: any) => {
        if (reqId !== listReqRef.current) return [];
        toast.error(friendlyApiError(err) || 'Unable to load disposition forms');
        return [];
      })
      .finally(() => {
        if (reqId === listReqRef.current) setTabLoading(false);
      });
  };

  const submissionsReqRef = useRef(0);
  const loadSubmissions = async (form: any, from: string, to: string) => {
    if (!form?.id || !from || !to) return;
    const reqId = ++submissionsReqRef.current;
    setSubmissionsLoading(true);
    try {
      const resp: any = await getApiCall(API_DISPOSITION_LIST, {
        form_id: form.id,
        from_date: from,
        to_date: to,
      });
      if (reqId !== submissionsReqRef.current) return;
      const fresh = Array.isArray(resp)
        ? resp.find((r: any) => String(r.id) === String(form.id))
        : null;
      setSubmissionsRow(fresh ? { ...form, ...fresh } : { ...form, disposition_form_data: [] });
    } catch (err: any) {
      if (reqId !== submissionsReqRef.current) return;
      toast.error(friendlyApiError(err) || 'Unable to load submissions');
    } finally {
      if (reqId === submissionsReqRef.current) setSubmissionsLoading(false);
    }
  };

  const handleSubmissionsRangeChange = (from: string, to: string) => {
    const { min, today } = getSubmissionsBounds();
    let nextFrom = from < min ? min : from;
    let nextTo = to > today ? today : to;
    if (nextFrom > nextTo) {
      if (from !== submissionsFrom) nextTo = nextFrom;
      else nextFrom = nextTo;
    }
    setSubmissionsFrom(nextFrom);
    setSubmissionsTo(nextTo);
    loadSubmissions(submissionsRow, nextFrom, nextTo);
  };

  const switchOwnerTab = (next: 'mine' | 'child') => {
    if (next === ownerTabRef.current) return;
    ownerTabRef.current = next;
    setOwnerTab(next);
    setSearch('');
    setDataList([]);
    setOriginalData([]);
    setTabLoading(true);
    fetchData(getUser(), next);
  };

  const closeSubmissionsModal = () => {
    submissionsReqRef.current += 1;
    setSubmissionsLoading(false);
    setShowSubmissionsModal(false);
    setSubmissionsRow(null);
    setSubmissionsSearch('');
    setSubmissionsSourceFilter('');
  };

  useEffect(() => {
    setLoading(true);
    const user = getUser();
    if (!user) return;
    if (user.role === USER_ROLES.SUPERADMIN) setIsSuperadmin(true);
    if (user?.acl_settings) {
      setLoggedInUserACL(parseModuleOperations(user.acl_settings.module_operations));
    }
    if (user.role === USER_ROLES.CALL_CENTRE_AGENT) setIsAgent(true);
    fetchData(user).finally(() => setLoading(false));
    getApiCall(API_DISPOSITION_TIMING_DEFAULTS)
      .then((resp: any) => {
        const rows = resp?.defaults || resp?.data?.defaults || [];
        const next: any = { inbound: '60', outbound: '60' };
        rows.forEach((row: any) => {
          if (row?.call_direction) {
            next[row.call_direction] = String(row.default_wrap_up_seconds ?? 60);
          }
        });
        setTimingDefaults(next);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const query = search.trim().toLowerCase();

    if (!query) {
      setDataList(originalData);
      return;
    }

    const filtered = originalData.filter((item: any) => {
      const searchableText = [item?.name, getCreatedByLabel(item)]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchableText.includes(query);
    });

    setDataList(filtered);
  }, [search, originalData]);

  useEffect(() => {
    if (!confirmDelete || !deleteId) return;

    postApiCall(API_DISPOSITION_DELETE, { dispositionFormIds: [deleteId] })
      .then(() => {
        toast.success('Disposition form deleted successfully');
        fetchData();
      })
      .catch((err: any) => toast.error(friendlyApiError(err) || 'Unable to delete disposition form'))
      .finally(() => {
        setConfirmDelete(false);
        setEnableDeleteModal(false);
        setDeleteId(null);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmDelete]);

  const isDispositionWriter = DISPOSITION_WRITE_ROLES.includes(getUser()?.role);
  const canAdd =
    isDispositionWriter && !!loggedInUserACL?.[MODULE_ACL.DISPOSITION_FORMS]?.add;
  const canEdit =
    isDispositionWriter && !!loggedInUserACL?.[MODULE_ACL.DISPOSITION_FORMS]?.edit;
  const canDelete =
    isDispositionWriter && !!loggedInUserACL?.[MODULE_ACL.DISPOSITION_FORMS]?.delete;

  const getFieldsArray = (rowData: any) => {
    if (Array.isArray(rowData?.fields)) return rowData.fields;
    if (typeof rowData?.fields === 'string') {
      try {
        const parsed = JSON.parse(rowData.fields);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const saveTimingDefaults = () => {
    const inbound = Number(timingDefaults.inbound);
    const outbound = Number(timingDefaults.outbound);
    if (
      !Number.isInteger(inbound) ||
      inbound < 1 ||
      inbound > 60 ||
      !Number.isInteger(outbound) ||
      outbound < 1 ||
      outbound > 60
    ) {
      toast.warning('Default wrap-up duration must be between 1 and 60 seconds');
      return;
    }
    setTimingDefaultsSaving(true);
    postApiCall(API_DISPOSITION_TIMING_DEFAULTS, {
      defaults: [
        { call_direction: 'inbound', default_wrap_up_seconds: inbound },
        { call_direction: 'outbound', default_wrap_up_seconds: outbound },
      ],
    })
      .then(() => toast.success('Default disposition timing updated'))
      .catch((err: any) => toast.error(friendlyApiError(err) || 'Unable to update default disposition timing'))
      .finally(() => setTimingDefaultsSaving(false));
  };

  const handleSetDefault = (rowData: any) => {
    if (!rowData?.id) return;
    const remove = Boolean(rowData?.is_default);
    setDefaultLoadingId(rowData.id);
    postApiCall(API_DISPOSITION_SET_DEFAULT.replace(':id', rowData.id), {
      remove_default: remove,
    })
      .then(() => {
        toast.success(
          remove ? 'Default disposition form removed' : 'Default disposition form updated',
        );
        fetchData();
      })
      .catch((err: any) =>
        toast.error(
          friendlyApiError(err) ||
            (remove
              ? 'Unable to remove default disposition form'
              : 'Unable to set default disposition form'),
        ),
      )
      .finally(() => setDefaultLoadingId(null));
  };

  const handleSetSystemDefault = (rowData: any) => {
    if (!rowData?.id) return;
    const remove = Boolean(rowData?.is_system_default);
    setSystemDefaultLoadingId(rowData.id);
    postApiCall(API_DISPOSITION_SET_SYSTEM_DEFAULT.replace(':id', rowData.id), {
      remove_default: remove,
    })
      .then(() => {
        toast.success(
          remove
            ? 'System Default disposition form removed'
            : 'System Default disposition form updated',
        );
        fetchData();
      })
      .catch((err: any) =>
        toast.error(
          friendlyApiError(err) ||
            (remove
              ? 'Unable to remove System Default disposition form'
              : 'Unable to set System Default disposition form'),
        ),
      )
      .finally(() => setSystemDefaultLoadingId(null));
  };

  const actionTemplate = (rowData: any) => (
    <div className="flex items-center gap-1.5">
      {!isAgent && canEdit && (
        <button
          type="button"
          className={`inline-flex h-8 w-8 items-center justify-center rounded-md ring-1 ring-inset transition disabled:cursor-not-allowed disabled:opacity-60 ${
            rowData?.is_default
              ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
              : 'bg-amber-100 text-amber-700 ring-amber-200 hover:bg-amber-200 dark:bg-amber-950/40 dark:text-amber-300'
          }`}
          title={
            rowData?.is_default
              ? `Default for ${formatDispositionCallDirectionLabel(rowData?.call_direction)} call flow — click to remove`
              : `Set as default for ${formatDispositionCallDirectionLabel(rowData?.call_direction)} call flow`
          }
          disabled={defaultLoadingId === rowData.id}
          onClick={() => handleSetDefault(rowData)}
        >
          <Star className="h-3.5 w-3.5" fill={rowData?.is_default ? 'currentColor' : 'none'} />
        </button>
      )}
      {isSuperAdmin && canEdit && (
        <button
          type="button"
          className={`inline-flex h-8 w-8 items-center justify-center rounded-md ring-1 ring-inset transition disabled:cursor-not-allowed disabled:opacity-60 ${
            // On: solid sky (same hue as the "System default" badge); off:
            // neutral grey, so the two states read apart at a glance.
            rowData?.is_system_default
              ? 'bg-sky-500 text-white ring-sky-600 hover:bg-sky-600 dark:bg-sky-600 dark:ring-sky-500'
              : 'bg-muted text-muted-foreground ring-border hover:bg-sky-100 hover:text-sky-700 dark:hover:bg-sky-950/40 dark:hover:text-sky-300'
          }`}
          aria-pressed={Boolean(rowData?.is_system_default)}
          title={
            rowData?.is_system_default
              ? `Current System Default for ${formatDispositionCallDirectionLabel(rowData?.call_direction)} call flow — click to remove`
              : `Set as System Default for ${formatDispositionCallDirectionLabel(rowData?.call_direction)} call flow (Super Admin)`
          }
          disabled={systemDefaultLoadingId === rowData.id}
          onClick={() => handleSetSystemDefault(rowData)}
        >
          <Globe className="h-3.5 w-3.5" />
        </button>
      )}
      <button
        type="button"
        className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-sky-100 text-sky-700 ring-1 ring-inset ring-sky-200 transition hover:bg-sky-200 dark:bg-sky-950/40 dark:text-sky-300"
        title="View"
        onClick={() => {
          setViewRow(rowData);
          setShowViewModal(true);
        }}
      >
        <Eye className="h-3.5 w-3.5" />
      </button>
      {canEdit && (
        <EditIcon
          linkTo={`/${ROUTES.DISPOSITION_FORMS.EDIT.replace(':id', rowData.id)}`}
        />
      )}
      {canDelete && (
        <DeleteIcon
          handleCallback={() => {
            setDeleteId(rowData.id);
            setEnableDeleteModal(true);
          }}
        />
      )}
    </div>
  );

  const defaultTemplate = (rowData: any) =>
    rowData?.is_default ? (
      <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300">
        Default ({formatDispositionCallDirectionLabel(rowData?.call_direction)})
      </span>
    ) : (
      <span className="text-muted-foreground">—</span>
    );

  const systemDefaultTemplate = (rowData: any) =>
    rowData?.is_system_default ? (
      <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/25">
        System Default ({formatDispositionCallDirectionLabel(rowData?.call_direction)})
      </span>
    ) : (
      <span className="text-muted-foreground">—</span>
    );

  const fieldsCountTemplate = (rowData: any) => (
    <span>{Array.isArray(rowData?.fields) ? rowData.fields.length : 0}</span>
  );

  const submissionsCountTemplate = (rowData: any) => {
    const currentUser = getUser();
    const submissions = Array.isArray(rowData?.disposition_form_data)
      ? rowData.disposition_form_data
      : [];
    const count = isSelfScopedAgent(currentUser)
      ? submissions.filter((s: any) => s.agent?.id === currentUser?.agentData?.id).length
      : submissions.length;
    return (
      <button
        type="button"
        className="inline-flex items-center gap-1 rounded-md bg-sky-100 px-2 py-1 text-[11px] font-semibold text-sky-700 ring-1 ring-inset ring-sky-200 transition hover:bg-sky-200 dark:bg-sky-950/40 dark:text-sky-300"
        title="View submissions"
        onClick={() => {
          const { weekStart, today } = getSubmissionsBounds();
          submissionsReqRef.current += 1;
          setSubmissionsLoading(false);
          setSubmissionsFrom(weekStart);
          setSubmissionsTo(today);
          setSubmissionsRow(rowData);
          setSubmissionsSearch('');
          setSubmissionsSourceFilter('');
          setShowSubmissionsModal(true);
        }}
      >
        <Table2 className="h-3 w-3" /> {count}
      </button>
    );
  };

  const formatSubmissionValue = (val: any) => {
    if (val === null || val === undefined || val === '') return '—';
    if (Array.isArray(val)) return val.join(', ');
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (typeof val === 'object') return JSON.stringify(val);
    return String(val);
  };

  const formatSubmissionFollowUpDate = (row: any) => {
    const date = row?.data?.follow_up_date;
    return date ? String(date).slice(0, 10) : '—';
  };

  const formatSubmissionFollowUpTime = (row: any) => {
    const time = row?.data?.follow_up_time;
    if (!time) return '—';
    const str = String(time).trim();
    return str.length >= 5 ? str.slice(0, 5) : str;
  };

  const sourceBadge = (src: any) => {
    if (!src) return <span className="text-muted-foreground">—</span>;
    const map: any = {
      campaign: 'bg-primary/10 text-primary ring-primary/25',
      system_default: 'bg-sky-100 text-sky-700 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300',
      call_center_default: 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300',
      call_group: 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300',
    };
    const cls = map[src] || 'bg-muted text-muted-foreground ring-border/60';
    return (
      <span
        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${cls}`}
      >
        {src}
      </span>
    );
  };

  const submissionSources = useMemo(() => {
    const arr = Array.isArray(submissionsRow?.disposition_form_data)
      ? submissionsRow.disposition_form_data
      : [];
    const set = new Set<string>();
    arr.forEach((r: any) => {
      if (r?.disposition_source) set.add(r.disposition_source);
    });
    return Array.from(set);
  }, [submissionsRow]);

  const submissionFieldNames = useMemo(() => {
    const fields = getFieldsArray(submissionsRow);
    const names = fields.map((f: any) => f?.field_name).filter(Boolean);
    if (names.length) return names;
    const arr = Array.isArray(submissionsRow?.disposition_form_data)
      ? submissionsRow.disposition_form_data
      : [];
    const set = new Set<string>();
    arr.forEach((r: any) => {
      if (r?.data && typeof r.data === 'object') {
        Object.keys(r.data).forEach((k) => {
          if (!FOLLOW_UP_DATA_KEYS.has(k)) set.add(k);
        });
      }
    });
    return Array.from(set);
  }, [submissionsRow]);

  const filteredSubmissions = useMemo(() => {
    const currentUser = getUser();
    let arr = Array.isArray(submissionsRow?.disposition_form_data)
      ? submissionsRow.disposition_form_data
      : [];
    if (isSelfScopedAgent(currentUser)) {
      arr = arr.filter((row: any) => row.agent?.id === currentUser?.agentData?.id);
    }
    const q = submissionsSearch.trim().toLowerCase();
    return arr
      .filter((row: any) => {
        if (
          submissionsSourceFilter &&
          row?.disposition_source !== submissionsSourceFilter
        ) {
          return false;
        }
        if (!q) return true;
        const haystack = [
          row?.id,
          row?.call_sid,
          row?.trace_id,
          row?.disposition_source,
          row?.agent?.agent_name,
          row?.agent?.agent_number,
          row?.agent?.id,
          row?.campaign?.campaign_name,
          row?.campaign?.campaign_id,
          row?.created_at,
          row?.updated_at,
          ...(row?.data && typeof row.data === 'object'
            ? Object.entries(row.data).flatMap(([k, v]) => [
                k,
                formatSubmissionValue(v),
              ])
            : []),
        ]
          .filter((v) => v !== null && v !== undefined)
          .map((v) => String(v).toLowerCase())
          .join(' | ');
        return haystack.includes(q);
      })
      .sort(
        (a: any, b: any) =>
          new Date(b?.created_at || 0).getTime() -
          new Date(a?.created_at || 0).getTime(),
      );
  }, [submissionsRow, submissionsSearch, submissionsSourceFilter]);

  const handleSubmissionsExport = () => {
    try {
      setSubmissionsExportDisabled(true);
      exportToCSV({
        fileName: `disposition-submissions-${submissionsRow?.name || submissionsRow?.id || 'form'}`,
        data: filteredSubmissions,
        columns: [
          { header: 'ID', accessor: (row: any) => row?.id ?? '-' },
          { header: 'Call ID', accessor: (row: any) => row?.call_sid ?? '-' },
          { header: 'Trace ID', accessor: (row: any) => row?.trace_id ?? '-' },
          {
            header: 'Disposition Source',
            accessor: (row: any) => row?.disposition_source ?? '-',
          },
          { header: 'Agent', accessor: (row: any) => row?.agent?.agent_name ?? '-' },
          {
            header: 'Agent Number',
            accessor: (row: any) => row?.agent?.agent_number ?? '-',
          },
          {
            header: 'Campaign',
            accessor: (row: any) => row?.campaign?.campaign_name ?? '-',
          },
          {
            header: 'Remarks Status',
            accessor: (row: any) => formatSubmissionValue(row?.data?.remarks_status),
          },
          ...submissionFieldNames.map((name: string) => ({
            header: name,
            accessor: (row: any) => formatSubmissionValue(row?.data?.[name]),
          })),
          {
            header: 'Created At',
            accessor: (row: any) => dateTemplate(row, 'created_at'),
          },
        ],
      });
    } finally {
      setSubmissionsExportDisabled(false);
    }
  };

  const openEditSubmission = (row: any) => {
    if (!row) return;
    const fields = getFieldsArray(submissionsRow);
    const data = row?.data && typeof row.data === 'object' ? row.data : {};
    const values: any = {};
    const masked = new Set<string>();
    fields.forEach((f: any) => {
      const name = f?.field_name;
      if (!name) return;
      const raw = data[name];
      // Masked reads (e.g. customer number) come back with '*'; keep them
      // locked so the agent can't accidentally overwrite the real value.
      if (typeof raw === 'string' && raw.includes('*')) masked.add(name);
      if (f?.field_type === 'Multi Select') {
        values[name] = Array.isArray(raw) ? raw : raw != null && raw !== '' ? [raw] : [];
      } else if (f?.field_type === 'Boolean') {
        values[name] = raw === true;
      } else {
        values[name] = raw === null || raw === undefined ? '' : String(raw);
      }
    });
    setEditRow(row);
    setEditValues(values);
    setEditMaskedFields(masked);
    const savedStatus = Array.isArray(data.remarks_status) ? data.remarks_status : [];
    setEditSelectedOutcomes(savedStatus.filter((v: any) => remarkOutcomeOptions.includes(v)));
    setEditOthersText(savedStatus.filter((v: any) => !remarkOutcomeOptions.includes(v)).join(', '));
    setShowEditModal(true);
  };

  const closeEditSubmission = () => {
    setShowEditModal(false);
    setEditRow(null);
    setEditValues({});
    setEditMaskedFields(new Set());
    setEditSelectedOutcomes([]);
    setEditOthersText('');
  };

  const toggleEditOutcome = (label: string) => {
    setEditSelectedOutcomes((prev) =>
      prev.includes(label) ? prev.filter((v) => v !== label) : [...prev, label],
    );
  };

  const saveEditSubmission = async () => {
    if (!editRow?.id) return;

    const remarks = [
      ...new Set([
        ...editSelectedOutcomes,
        ...editOthersText.split(',').map((s) => s.trim()).filter(Boolean),
      ]),
    ];
    if (remarks.length === 0) {
      toast.warning('Pick at least one Remarks Status outcome (or type one under Others)');
      return;
    }

    const fields = getFieldsArray(submissionsRow);
    const data: any = { remarks_status: remarks };

    for (const f of fields) {
      const name = f?.field_name;
      if (!name) continue;
      // Masked fields stay untouched; backend preserves the real stored value.
      if (editMaskedFields.has(name)) continue;
      const type = f?.field_type;
      const val = editValues[name];
      const required = f?.is_required === true;

      if (type === 'Boolean') {
        data[name] = val === true;
        continue;
      }
      if (type === 'Multi Select') {
        const arr = Array.isArray(val) ? val : [];
        if (required && arr.length === 0) {
          toast.warning(`${name} is required`);
          return;
        }
        data[name] = arr;
        continue;
      }

      const str = val === null || val === undefined ? '' : String(val).trim();
      if (str === '') {
        if (required) {
          toast.warning(`${name} is required`);
          return;
        }
        // Was `continue` (skip the key entirely) on the assumption the
        // backend would clear an optional field on its own when it's
        // absent from the payload — it doesn't, it just leaves whatever
        // was saved before untouched, so clearing an optional field back
        // out never actually took in the DB. Send the empty value instead.
        data[name] = '';
        continue;
      }
      if (type === 'Integer') {
        const num = Number(str);
        if (!Number.isInteger(num)) {
          toast.warning(`${name} must be an integer`);
          return;
        }
        data[name] = num;
      } else if (type === 'Float') {
        const num = Number(str);
        if (!Number.isFinite(num)) {
          toast.warning(`${name} must be a number`);
          return;
        }
        data[name] = num;
      } else {
        data[name] = str;
      }
    }

    try {
      setEditSaving(true);
      await patchApiCall(
        API_DISPOSITION_FORM_DATA_UPDATE.replace(':id', editRow.id),
        { data },
      );
      toast.success('Submission updated successfully');
      await Promise.all([
        loadSubmissions(submissionsRow, submissionsFrom, submissionsTo),
        fetchData(getUser()),
      ]);
      closeEditSubmission();
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Unable to update submission');
    } finally {
      setEditSaving(false);
    }
  };

  const renderEditField = (f: any) => {
    const name = f?.field_name;
    if (!name) return null;
    const type = f?.field_type;
    const isMasked = editMaskedFields.has(name);
    const label = `${name}${f?.is_required ? ' *' : ''}`;

    if (isMasked) {
      const raw = editRow?.data?.[name];
      return (
        <Form.Group className="mb-3" key={name}>
          <Form.Label>{label}</Form.Label>
          <Form.Control
            type="text"
            value={raw === null || raw === undefined ? '' : String(raw)}
            disabled
            readOnly
          />
          <small className="text-muted">Masked — cannot be edited</small>
        </Form.Group>
      );
    }

    if (type === 'Boolean') {
      return (
        <Form.Group className="mb-3" key={name}>
          <Form.Check
            type="checkbox"
            label={label}
            checked={editValues[name] === true}
            onChange={(e) =>
              setEditValues((prev: any) => ({ ...prev, [name]: e.target.checked }))
            }
          />
        </Form.Group>
      );
    }

    if (type === 'Multi Select') {
      const options = Array.isArray(f?.options) ? f.options : [];
      const selected = Array.isArray(editValues[name]) ? editValues[name] : [];
      return (
        <Form.Group className="mb-3" key={name}>
          <Form.Label>{label}</Form.Label>
          <div>
            {options.map((opt: any) => {
              const optStr = String(opt);
              const checked = selected.includes(optStr);
              return (
                <Form.Check
                  inline
                  key={optStr}
                  type="checkbox"
                  label={optStr}
                  checked={checked}
                  onChange={(e) =>
                    setEditValues((prev: any) => {
                      const cur = Array.isArray(prev[name]) ? prev[name] : [];
                      const next = e.target.checked
                        ? [...cur, optStr]
                        : cur.filter((x: string) => x !== optStr);
                      return { ...prev, [name]: next };
                    })
                  }
                />
              );
            })}
          </div>
        </Form.Group>
      );
    }

    if (type === 'Textarea') {
      return (
        <Form.Group className="mb-3" key={name}>
          <Form.Label>{label}</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={editValues[name] ?? ''}
            onChange={(e: any) =>
              setEditValues((prev: any) => ({ ...prev, [name]: e.target.value }))
            }
          />
        </Form.Group>
      );
    }

    const inputType =
      type === 'Integer' || type === 'Float'
        ? 'number'
        : type === 'Date'
        ? 'date'
        : 'text';

    return (
      <Form.Group className="mb-3" key={name}>
        <Form.Label>{label}</Form.Label>
        <Form.Control
          type={inputType}
          value={editValues[name] ?? ''}
          onChange={(e) =>
            setEditValues((prev: any) => ({ ...prev, [name]: e.target.value }))
          }
        />
      </Form.Group>
    );
  };

  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] =
    useState<any>({});

  useEffect(() => {
    const user = getUser();
    if (user) {
      if (user?.acl_settings) {
        setModuleOperationsForReadPermissionCheck(
          parseModuleOperations(user.acl_settings.module_operations),
        );
      }
    }
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setLoggedInUserACL(parsed);
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  const dateTemplate = (rowData: any, field: string) => {
    const value = rowData?.[field];
    if (!value) return '—';

    let raw: any = value;
    if (typeof value === 'string') {
      const t = value.trim();
      const pre = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4}),?\s+(\d{1,2}):(\d{2}):(\d{2})\s*(am|pm)$/i);
      if (pre) {
        const [, dd, mm, yyyy, hh, mi, ss, ap] = pre;
        let h = Number(hh) % 12;
        if (ap.toLowerCase() === 'pm') h += 12;
        raw = `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}T${String(h).padStart(2, '0')}:${mi}:${ss}+05:30`;
      } else if (!/(Z|[+-]\d{2}:?\d{2})$/i.test(t)) {
        raw = `${t.replace(' ', 'T')}Z`;
      }
    }
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return '—';
    return parsed.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const callFlowTemplate = (rowData: any) =>
    formatDispositionCallDirectionLabel(rowData?.call_direction);

  const wrapUpTemplate = (rowData: any) => {
    if (rowData?.wrap_up_seconds != null && rowData?.wrap_up_seconds !== '') {
      return `${rowData.wrap_up_seconds}s`;
    }
    if (rowData?.use_system_wrap_up_default === false) {
      return 'Not set';
    }
    const dir = wrapUpTimingDirection(rowData?.call_direction);
    return `Default (${timingDefaults[dir] || 60}s)`;
  };

  const createdByBody = (rowData: any) => <span>{getCreatedByLabel(rowData)}</span>;

  const canShowCreatedByFilter = useMemo(() => isSuperAdmin, [isSuperAdmin]);

  const handleExport = async () => {
    try {
      setButtonDisable(true);

      exportToCSV({
        fileName: 'disposition-form-list',
        data: dataList,
        columns: [
          { header: 'Name', accessor: (row: any) => row.name || '-' },
          { header: 'Default', accessor: (row: any) => (row.is_default ? 'Yes' : 'No') },
          {
            header: 'System Default',
            accessor: (row: any) => (row.is_system_default ? 'Yes' : 'No'),
          },
          ...(isSuperAdmin
            ? [
                {
                  header: 'Created By',
                  accessor: (row: any) =>
                    row.creator?.username || row.created_by?.username || '-',
                },
              ]
            : []),
          { header: 'Fields', accessor: (row: any) => row.fields?.length || 0 },
          {
            header: 'Created Date',
            accessor: (row: any) => dateTemplate(row, 'created_at'),
          },
          {
            header: 'Updated Date',
            accessor: (row: any) => dateTemplate(row, 'updated_at'),
          },
        ],
      });
    } finally {
      setButtonDisable(false);
    }
  };

  const canRead = hasPermission(
    moduleOperationsForReadPermissionCheck,
    MODULE_ACL.DISPOSITION_FORMS,
    'read',
  );

  if (!canRead) {
    return (
      <div
        className="text-center p-4"
        style={{ marginTop: '50px', marginBottom: '50px', fontSize: '16px' }}
      >
        No read permission found in assigned ACL
      </div>
    );
  }

  if (loading) return <Loader />;

  const currentUserForModal = getUser();
  const selfScopedAgent = isSelfScopedAgent(currentUserForModal);
  const showEditMetaColumns = canSeeEditMeta(currentUserForModal);

  const listColumns: ColumnDef<any>[] = [
    { accessorKey: 'name', header: 'Name' },
    ...(!isSuperAdmin && !isAgent
      ? [
          {
            id: 'is_default',
            header: 'Default',
            enableSorting: false,
            cell: ({ row }: any) => defaultTemplate(row.original),
          } as ColumnDef<any>,
        ]
      : []),
    ...(isSuperAdmin && !isAgent
      ? [
          {
            id: 'is_system_default',
            header: 'System Default',
            enableSorting: false,
            cell: ({ row }: any) => systemDefaultTemplate(row.original),
          } as ColumnDef<any>,
        ]
      : []),
    {
      id: 'call_direction',
      header: 'Call flow',
      cell: ({ row }: any) => callFlowTemplate(row.original),
    },
    {
      id: 'wrap_up',
      header: 'Wrap-up time',
      enableSorting: false,
      cell: ({ row }: any) => wrapUpTemplate(row.original),
    },
    ...(isSuperAdmin
      ? [
          {
            id: 'created_by',
            header: 'Created By',
            enableSorting: false,
            cell: ({ row }: any) => createdByBody(row.original),
          } as ColumnDef<any>,
        ]
      : []),
    {
      id: 'fields_count',
      header: 'Fields',
      enableSorting: false,
      cell: ({ row }: any) => fieldsCountTemplate(row.original),
    },
    {
      id: 'submissions',
      header: 'Submissions (This Week)',
      enableSorting: false,
      cell: ({ row }: any) => submissionsCountTemplate(row.original),
    },
    ...(!isAgent
      ? [
          {
            accessorKey: 'created_at',
            header: 'Created Date',
            cell: ({ row }: any) => dateTemplate(row.original, 'created_at'),
          } as ColumnDef<any>,
          {
            accessorKey: 'updated_at',
            header: 'Updated Date',
            cell: ({ row }: any) => dateTemplate(row.original, 'updated_at'),
          } as ColumnDef<any>,
        ]
      : []),
    {
      id: 'action',
      header: 'Action',
      enableSorting: false,
      cell: ({ row }: any) => actionTemplate(row.original),
    },
  ];

  const submissionsColumns: ColumnDef<any>[] = [
    { accessorKey: 'id', header: 'ID' },
    {
      id: 'source',
      header: 'Source',
      cell: ({ row }: any) => sourceBadge(row.original?.disposition_source),
    },
    {
      id: 'call_sid',
      header: 'Call ID',
      cell: ({ row }: any) => (
        <span className="font-mono text-xs">{row.original?.call_sid || '—'}</span>
      ),
    },
    {
      id: 'trace_id',
      header: 'Trace ID',
      cell: ({ row }: any) => (
        <span className="font-mono text-xs">{row.original?.trace_id || '—'}</span>
      ),
    },
    {
      id: 'agent',
      header: 'Agent',
      cell: ({ row }: any) =>
        row.original?.agent?.agent_name
          ? `${row.original.agent.agent_name}${
              row.original?.agent?.agent_number
                ? ` (${row.original.agent.agent_number})`
                : ''
            }`
          : '—',
    },
    {
      id: 'campaign',
      header: 'Campaign',
      cell: ({ row }: any) => row.original?.campaign?.campaign_name || '—',
    },
    {
      id: 'remarks_status',
      header: 'Remarks Status',
      cell: ({ row }: any) => formatSubmissionValue(row.original?.data?.remarks_status),
    },
    ...submissionFieldNames.map(
      (name: string) =>
        ({
          id: `f_${name}`,
          header: name,
          enableSorting: false,
          cell: ({ row }: any) => formatSubmissionValue(row.original?.data?.[name]),
        }) as ColumnDef<any>,
    ),
    ...(submissionsRow?.follow_ups_enabled === true
      ? ([
          {
            id: 'follow_up_date',
            header: 'Follow-Up Date',
            enableSorting: false,
            cell: ({ row }: any) => formatSubmissionFollowUpDate(row.original),
          },
          {
            id: 'follow_up_time',
            header: 'Follow-Up Time',
            enableSorting: false,
            cell: ({ row }: any) => formatSubmissionFollowUpTime(row.original),
          },
        ] as ColumnDef<any>[])
      : []),
    {
      accessorKey: 'created_at',
      header: 'Created At',
      cell: ({ row }: any) => dateTemplate(row.original, 'created_at'),
    },
    ...(showEditMetaColumns
      ? [
          {
            id: 'auto_submit',
            header: 'Auto Submit',
            enableSorting: false,
            cell: ({ row }: any) =>
              row.original?.is_auto_submit ? (
                <span className="inline-flex items-center rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700 ring-1 ring-inset ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300">
                  Yes
                </span>
              ) : (
                <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground ring-1 ring-inset ring-border/60">
                  No
                </span>
              ),
          } as ColumnDef<any>,
          {
            id: 'edited',
            header: 'Edited',
            enableSorting: false,
            cell: ({ row }: any) =>
              row.original?.edited_at ? (
                <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700 ring-1 ring-inset ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300">
                  Yes
                </span>
              ) : (
                <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground ring-1 ring-inset ring-border/60">
                  No
                </span>
              ),
          } as ColumnDef<any>,
          {
            id: 'edited_on',
            header: 'Edited On',
            enableSorting: false,
            cell: ({ row }: any) =>
              row.original?.edited_at ? dateTemplate(row.original, 'edited_at') : '—',
          } as ColumnDef<any>,
          {
            id: 'edited_by',
            header: 'Edited By',
            enableSorting: false,
            cell: ({ row }: any) =>
              row.original?.edited_at
                ? row.original?.editor?.username || row.original?.edited_by || '—'
                : '—',
          } as ColumnDef<any>,
        ]
      : []),
    ...(selfScopedAgent
      ? [
          {
            id: 'sub_action',
            header: 'Action',
            enableSorting: false,
            cell: ({ row }: any) => {
              const editable = isWithinEditWindow(row.original);
              return (
                <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition hover:bg-primary/15 disabled:cursor-not-allowed disabled:opacity-40"
                  title={
                    editable ? 'Edit submission' : 'Edit window expired (24 hours)'
                  }
                  disabled={!editable}
                  onClick={() => openEditSubmission(row.original)}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              );
            },
          } as ColumnDef<any>,
        ]
      : []),
  ];

  return (
    <FillPage>
      <PageHeader
        title="Disposition Forms"
        description="Customer-touch outcome forms shown to agents at end-of-call."
        actions={
          <>
            <Button
              variant="outline"
              size="icon"
              disabled={loading}
              onClick={() => {
                setLoading(true);
                const user = getUser();
                fetchData(user).finally(() => setLoading(false));
              }}
              title="Refresh"
              aria-label="Refresh"
            >
              <RefreshCw className={loading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
            </Button>
            {dataList?.length > 0 && !isAgent && (
              <Button variant="outline" disabled={buttonDisable} onClick={handleExport}>
                <Download className="h-4 w-4" />
                Export
              </Button>
            )}
            {canAdd && (
              <Button onClick={() => navigate(`/${ROUTES.DISPOSITION_FORMS.CREATE}`)}>
                <Plus className="h-4 w-4" />
                Create
              </Button>
            )}
          </>
        }
      />
      {isSuperAdmin && canEdit && (
        <div
          className="mb-2 rounded-lg border border-border/60 bg-card px-3 py-1.5"
          style={{
            boxShadow:
              '4px 4px 10px rgba(163, 177, 198, 0.28), -4px -4px 10px rgba(255, 255, 255, 0.85)',
          }}
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Default wrap-up
            </span>
            <div className="flex items-center gap-1">
              <label className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                In
              </label>
              <input
                type="number"
                min={1}
                max={60}
                value={timingDefaults.inbound}
                onChange={(e) =>
                  setTimingDefaults((prev: any) => ({
                    ...prev,
                    inbound: e.target.value,
                  }))
                }
                className="input-embossed h-7 w-16 rounded-md px-2 text-xs"
              />
            </div>
            <div className="flex items-center gap-1">
              <label className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                Out
              </label>
              <input
                type="number"
                min={1}
                max={60}
                value={timingDefaults.outbound}
                onChange={(e) =>
                  setTimingDefaults((prev: any) => ({
                    ...prev,
                    outbound: e.target.value,
                  }))
                }
                className="input-embossed h-7 w-16 rounded-md px-2 text-xs"
              />
            </div>
            <Button
              variant="primary"
              size="sm"
              className="h-7 px-2.5 text-xs"
              disabled={timingDefaultsSaving}
              onClick={saveTimingDefaults}
            >
              {timingDefaultsSaving ? 'Saving…' : 'Save'}
            </Button>
            <span
              className="ml-auto truncate text-[11px] text-muted-foreground"
              title='Default wrap-up (seconds) applied when a form has no custom duration. ACL "Custom timing" lets users override per form.'
            >
              Applies when a form has no custom duration.
            </span>
          </div>
        </div>
      )}

      {isSuperAdmin && (
        <Tabs
          value={ownerTab}
          onValueChange={(value) => switchOwnerTab(value as 'mine' | 'child')}
          className="mb-4"
        >
          <TabsList className="h-auto gap-1 rounded-full border border-border/60 bg-muted/40 p-1 shadow-[inset_2px_2px_5px_rgba(163,177,198,0.35),inset_-2px_-2px_5px_rgba(255,255,255,0.9)]">
            <TabsTrigger
              value="mine"
              className="gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground data-[state=active]:bg-primary/15 data-[state=active]:!text-primary data-[state=active]:font-semibold data-[state=active]:shadow-[2px_2px_5px_rgba(163,177,198,0.55),-2px_-2px_5px_rgba(255,255,255,0.9)]"
            >
              <User className="h-3.5 w-3.5" />
              Created by superadmin
            </TabsTrigger>
            <TabsTrigger
              value="child"
              className="gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground data-[state=active]:bg-primary/15 data-[state=active]:!text-primary data-[state=active]:font-semibold data-[state=active]:shadow-[2px_2px_5px_rgba(163,177,198,0.55),-2px_-2px_5px_rgba(255,255,255,0.9)]"
            >
              <Users className="h-3.5 w-3.5" />
              Created by child users
            </TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      <DataTable
        key={isSuperAdmin ? ownerTab : 'all'}
        tableId="disposition-forms"
        columns={listColumns}
        data={dataList}
        isLoading={tabLoading}
        pageSize={10}
        emptyTitle={
          isSuperAdmin && ownerTab === 'child'
            ? 'No disposition forms created by child users'
            : 'No disposition forms'
        }
        emptyDescription={
          isSuperAdmin && ownerTab === 'child'
            ? 'None of your child users have created a disposition form yet.'
            : 'Nothing to show with the current filters.'
        }
        disableExport
      />

      <Modal
        show={showViewModal}
        onHide={() => {
          setShowViewModal(false);
          setViewRow(null);
        }}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="border-b border-border/50 pb-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-inset ring-primary/25">
              <ClipboardList className="h-5 w-5" />
            </div>
            <div>
              <Modal.Title className="text-base font-bold text-foreground">
                Disposition Form Details
              </Modal.Title>
              <p className="text-[11px] text-muted-foreground">
                Fields and flags for {viewRow?.name || 'this form'}.
              </p>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-4 grid grid-cols-1 gap-2 rounded-lg border border-border/60 bg-muted/30 p-3 sm:grid-cols-2">
            <div className="flex items-start gap-2 text-xs">
              <span className="min-w-[80px] font-semibold uppercase tracking-wide text-[10px] text-muted-foreground">
                Name
              </span>
              <span className="text-foreground">{viewRow?.name || '—'}</span>
            </div>
            <div className="flex items-start gap-2 text-xs">
              <span className="min-w-[80px] font-semibold uppercase tracking-wide text-[10px] text-muted-foreground">
                Call flow
              </span>
              <span className="text-foreground">{callFlowTemplate(viewRow)}</span>
            </div>
            <div className="flex items-start gap-2 text-xs">
              <span className="min-w-[80px] font-semibold uppercase tracking-wide text-[10px] text-muted-foreground">
                Default
              </span>
              {viewRow?.is_default ? (
                <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300">
                  Yes ({formatDispositionCallDirectionLabel(viewRow?.call_direction)})
                </span>
              ) : (
                <span className="text-muted-foreground">No</span>
              )}
            </div>
            <div className="flex items-start gap-2 text-xs">
              <span className="min-w-[80px] font-semibold uppercase tracking-wide text-[10px] text-muted-foreground">
                Sys. Default
              </span>
              {viewRow?.is_system_default ? (
                <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/25">
                  Yes ({formatDispositionCallDirectionLabel(viewRow?.call_direction)})
                </span>
              ) : (
                <span className="text-muted-foreground">No</span>
              )}
            </div>
            {isSuperAdmin && (
              <div className="flex items-start gap-2 text-xs sm:col-span-2">
                <span className="min-w-[80px] font-semibold uppercase tracking-wide text-[10px] text-muted-foreground">
                  Created By
                </span>
                <span className="text-foreground">{getCreatedByLabel(viewRow)}</span>
              </div>
            )}
          </div>

          <div className="mb-2 flex items-center justify-between">
            <h6 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Fields
            </h6>
            <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/25">
              {getFieldsArray(viewRow).length} total
            </span>
          </div>

          <div className="overflow-x-auto rounded-lg border border-border/60 bg-card">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-primary/[0.06] text-[10px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2 text-left font-semibold">Field Name</th>
                  <th className="px-3 py-2 text-left font-semibold">Type</th>
                  <th className="px-3 py-2 text-left font-semibold">Required</th>
                  <th className="px-3 py-2 text-left font-semibold">Attach to CDR</th>
                  <th className="px-3 py-2 text-left font-semibold">Default</th>
                  <th className="px-3 py-2 text-left font-semibold">Min</th>
                  <th className="px-3 py-2 text-left font-semibold">Max</th>
                </tr>
              </thead>
              <tbody>
                {getFieldsArray(viewRow).length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-3 py-6 text-center text-muted-foreground"
                    >
                      No fields found
                    </td>
                  </tr>
                ) : (
                  getFieldsArray(viewRow).map((f: any, idx: number) => (
                    <tr
                      key={idx}
                      className={`border-t border-border/40 ${
                        idx % 2 ? 'bg-muted/30' : ''
                      }`}
                    >
                      <td className="px-3 py-2 font-medium text-foreground">
                        {f?.field_name ?? '—'}
                      </td>
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-foreground ring-1 ring-inset ring-border/60">
                          {f?.field_type
                            ? TYPE_LABELS[f.field_type] || f.field_type
                            : '—'}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        {f?.is_required ? (
                          <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700 ring-1 ring-inset ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300">
                            Yes
                          </span>
                        ) : (
                          <span className="text-muted-foreground">No</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {f?.attach_to_cdr ? (
                          <span className="inline-flex items-center rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold text-sky-700 ring-1 ring-inset ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300">
                            Yes
                          </span>
                        ) : (
                          <span className="text-muted-foreground">No</span>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px] text-foreground">
                        {f?.field_default !== undefined && f?.field_default !== null
                          ? String(f?.field_default)
                          : '—'}
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px] text-foreground">
                        {f?.field_min ?? '—'}
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px] text-foreground">
                        {f?.field_max ?? '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-t border-border/50 pt-3">
          <Button
            variant="outline"
            onClick={() => {
              setShowViewModal(false);
              setViewRow(null);
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showSubmissionsModal}
        onHide={closeSubmissionsModal}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Submissions {submissionsRow?.name ? `– ${submissionsRow.name}` : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
            <Form.Control
              className="w-25"
              placeholder="Search submissions..."
              value={submissionsSearch}
              onChange={(e) => setSubmissionsSearch(e.target.value)}
            />
            <Form.Select
              className="w-auto"
              value={submissionsSourceFilter}
              onChange={(e) => setSubmissionsSourceFilter(e.target.value)}
            >
              <option value="">All sources</option>
              {submissionSources.map((s: string) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Form.Select>
            <div className="flex items-center gap-1.5">
              <CalendarDateInput
                aria-label="Submissions from date"
                value={submissionsFrom}
                onChange={(v) => handleSubmissionsRangeChange(v, submissionsTo)}
                min={getSubmissionsBounds().min}
                max={submissionsTo || getSubmissionsBounds().today}
                disabled={submissionsLoading}
                contentClassName="!z-[2100]"
              />
              <span className="text-xs text-muted-foreground">to</span>
              <CalendarDateInput
                aria-label="Submissions to date"
                value={submissionsTo}
                onChange={(v) => handleSubmissionsRangeChange(submissionsFrom, v)}
                min={submissionsFrom || getSubmissionsBounds().min}
                max={getSubmissionsBounds().today}
                disabled={submissionsLoading}
                contentClassName="!z-[2100]"
              />
            </div>
            <div className="ms-auto">
              <Button
                variant="secondary"
                disabled={
                  submissionsLoading ||
                  submissionsExportDisabled ||
                  filteredSubmissions.length === 0
                }
                onClick={handleSubmissionsExport}
              >
                Export
              </Button>
            </div>
          </div>

          <DataTable
            tableId="disposition-form-submissions"
            columns={submissionsColumns}
            data={filteredSubmissions}
            isLoading={submissionsLoading}
            pageSize={10}
            emptyTitle="No submissions found"
            disableExport
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeSubmissionsModal}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showEditModal} onHide={closeEditSubmission} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Submission</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-info py-2">
            You can edit this submission only within 24 hours of submitting it.
          </div>
          <Form.Group className="mb-3">
            <Form.Label>Remarks Status *</Form.Label>
            {/* Same options an admin/SA configured for the live-call
              * disposition card (DispositionCard.tsx) — shown here too so
              * an agent editing their own submission sees what was
              * actually available/selected, not just a blank text box. */}
            <div className="mb-2" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {remarkOutcomeOptions.map((o) => (
                <Button
                  key={o}
                  type="button"
                  size="sm"
                  variant={editSelectedOutcomes.includes(o) ? 'default' : 'outline'}
                  onClick={() => toggleEditOutcome(o)}
                >
                  {o}
                </Button>
              ))}
            </div>
            <Form.Control
              type="text"
              value={editOthersText}
              placeholder="Others (comma separated)"
              onChange={(e) => setEditOthersText(e.target.value)}
            />
          </Form.Group>
          {getFieldsArray(submissionsRow).map((f: any) => renderEditField(f))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeEditSubmission} disabled={editSaving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveEditSubmission} disabled={editSaving}>
            {editSaving ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>

      <RenderDeleteModal
        show={enableDeleteModal}
        setShow={setEnableDeleteModal}
        setConfirmDelete={setConfirmDelete}
      />
    </FillPage>
  );
};

export default DispositionFormsList;
